import { useCallback, useEffect, useState } from 'react';

import { ICollaboratorPlayerData } from '../../interfaces/collaborators';
import Companies from '../../services/api/collaborators/collaborators';
import { useIsMounted } from '../useIsMounted';

export interface ICollaboratorPlayerDataState {
  data: ICollaboratorPlayerData | undefined;
  isLoading: boolean;
  error: boolean;
}

const useCollaboratorPlayerData = (companyId: string, email: string, enabled: boolean) => {
  const [playerDataState, setPlayerDataState] = useState<ICollaboratorPlayerDataState>({
    data: undefined,
    isLoading: enabled,
    error: false
  });

  const isMounted = useIsMounted();

  const fetchData = useCallback(async () => {
    setPlayerDataState((prevState) => ({ ...prevState, isLoading: true, error: false }));

    let response: ICollaboratorPlayerData | undefined = undefined;
    let hasError = false;

    try {
      response = await Companies.getCollaboratorPlayerData(email, companyId);
    } catch {
      hasError = true;
    } finally {
      if (isMounted) {
        setPlayerDataState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: hasError,
          data: hasError ? prevState.data : response
        }));
      }
    }
  }, [companyId, email, isMounted]);

  useEffect(() => {
    if (!enabled) {
      setPlayerDataState({ data: undefined, isLoading: false, error: false });
      return;
    }
    void fetchData();
  }, [fetchData, enabled]);

  return { playerDataState, refetchPlayerData: fetchData };
};

export default useCollaboratorPlayerData;
